import { ChartLineUpIcon, GiftIcon, PackageIcon, PercentIcon, StarIcon, TagIcon, TrendUpIcon } from "@phosphor-icons/react"
import dayjs from "dayjs"
import type { TFunction } from "i18next"
import type { ReactNode } from "react"
import type { ApplicablePromotion, PriceBreakdown, PromotionPriceInfo } from "../../../apis/pricing"
import { currencyFormat } from "../../../utils"

// How the buyer side reads a price-engine breakdown. One place, so the card,
// the table, the product page and the promotion strip say the same thing.
//
// Rules (canvas OrdB_Catalog / OrdB_PromoCatalog, findings B12/B31):
// - a promotion label appears only when a promotion actually set the price
//   (selectedPriceType PROMOTION) or a non-price promotion is attached;
// - the strike-through is the BASE price the winning price replaced, never the
//   retail (b2c) price;
// - the retail price is a grey reference line, never a discount.

export const formatDate = (value?: string | null): string => {
  if (!value) return "-"
  const d = dayjs(value)
  return d.isValid() ? d.format("YYYY-MM-DD") : "-"
}

export type PromoKind = "DISCOUNT" | "FIXED" | "TIER" | "FREE_GOODS" | "BUNDLE" | "THRESHOLD"

export const PROMO_KINDS: PromoKind[] = ["DISCOUNT", "FIXED", "TIER", "FREE_GOODS", "BUNDLE", "THRESHOLD"]

export const promoIcon = (kind: string, size = 14): ReactNode => {
  switch (kind) {
    case "DISCOUNT":
      return <PercentIcon size={size} />
    case "FIXED":
      return <TagIcon size={size} />
    case "TIER":
      return <TrendUpIcon size={size} />
    case "FREE_GOODS":
      return <GiftIcon size={size} />
    case "BUNDLE":
      return <PackageIcon size={size} />
    case "THRESHOLD":
      return <ChartLineUpIcon size={size} />
    case "SPECIAL":
    case "SEGMENT":
      return <StarIcon size={size} />
    default:
      return <TagIcon size={size} />
  }
}

export type PriceTag = { kind: string; label: string }

export type PriceView = {
  /** Final unit price; 0 when nothing resolves (rendered as «Үнэ тодорхойгүй»). */
  price: number
  /** Base price the winning price replaced (shown struck through). */
  strike: number | null
  /** Second line under the price. */
  source: string
  /** True when the source line names a promotion / special / segment price. */
  sourceIsPromo: boolean
  tag: PriceTag | null
  /** Retail (b2c) reference price, if the supplier keeps one. */
  retail: number | null
  /** The price request for this supplier failed: show «-», not a fallback price (B03). */
  unavailable?: boolean
}

export const unavailablePrice = (t: TFunction): PriceView => ({
  price: 0,
  strike: null,
  source: t("order:catalog.priceNotLoaded"),
  sourceIsPromo: false,
  tag: null,
  retail: null,
  unavailable: true,
})

const pct = (value: number | null | undefined) => (value ? Math.round(Number(value) * 100) / 100 : null)

export const promotionTag = (t: TFunction, promo: PromotionPriceInfo): PriceTag => {
  const kind = promo.pricingType
  if (kind === "DISCOUNT") {
    if (pct(promo.discountPercentage)) return { kind, label: t("order:catalog.tag.discountPct", { pct: pct(promo.discountPercentage) }) }
    if (promo.discountAmount) return { kind, label: t("order:catalog.tag.discountAmount", { amount: currencyFormat(promo.discountAmount) }) }
    return { kind, label: t("order:catalog.tag.discount") }
  }
  if (kind === "FIXED") return { kind, label: t("order:catalog.tag.fixed") }
  if (kind === "TIER") {
    return pct(promo.discountPercentage)
      ? { kind, label: t("order:catalog.tag.tierPct", { pct: pct(promo.discountPercentage) }) }
      : { kind, label: t("order:catalog.tag.tier") }
  }
  return { kind, label: t("order:catalog.promoDesc.generic") }
}

export const nonPriceTag = (t: TFunction, promo: ApplicablePromotion): PriceTag => {
  if (promo.pricingType === "FREE_GOODS") {
    const rule = promo.freeGoodsRule
    return rule
      ? { kind: "FREE_GOODS", label: t("order:catalog.tag.giftRule", { buy: rule.buyQuantity, get: rule.getQuantity }) }
      : { kind: "FREE_GOODS", label: t("order:catalog.tag.gift") }
  }
  if (promo.pricingType === "BUNDLE") return { kind: "BUNDLE", label: t("order:catalog.tag.bundle") }
  return { kind: "THRESHOLD", label: t("order:catalog.tag.threshold") }
}

export const describePrice = (t: TFunction, breakdown: PriceBreakdown | undefined, fallbackPrice: number): PriceView => {
  const price = breakdown && breakdown.finalPrice > 0 ? breakdown.finalPrice : fallbackPrice
  const base = breakdown?.originalBasePrice ?? null
  const retail = breakdown?.b2cPrice && breakdown.b2cPrice > 0 ? breakdown.b2cPrice : null
  const nonPrice = (breakdown?.applicablePromotions || []).find((p) =>
    ["FREE_GOODS", "BUNDLE", "THRESHOLD"].includes(p.pricingType),
  )
  const gift = (breakdown?.applicablePromotions || []).find((p) => p.pricingType === "FREE_GOODS" && p.freeGoodsRule)
  const giftSuffix = gift?.freeGoodsRule
    ? ` · ${t("order:catalog.giftLine", { buy: gift.freeGoodsRule.buyQuantity, get: gift.freeGoodsRule.getQuantity })}`
    : ""

  const type = breakdown?.selectedPriceType
  const replacedBase = base && base > price ? base : null

  if (type === "PROMOTION" && breakdown?.promotionPrice) {
    const promo = breakdown.promotionPrice
    const source = promo.effectiveTo
      ? t("order:catalog.promoUntil", { name: promo.promotionName, date: formatDate(promo.effectiveTo) })
      : promo.promotionName
    return { price, strike: replacedBase, source, sourceIsPromo: true, tag: promotionTag(t, promo), retail }
  }
  if (type === "SPECIAL") {
    return {
      price,
      strike: replacedBase,
      source: t("order:catalog.specialPrice"),
      sourceIsPromo: true,
      tag: { kind: "SPECIAL", label: t("order:catalog.tag.special") },
      retail,
    }
  }
  if (type === "SEGMENT") {
    const name = breakdown?.segmentPrice?.segmentName
    return {
      price,
      strike: replacedBase,
      source: name ? `${t("order:catalog.segmentPrice")} · ${name}` : t("order:catalog.segmentPrice"),
      sourceIsPromo: true,
      tag: { kind: "SEGMENT", label: t("order:catalog.tag.segment") },
      retail,
    }
  }
  return {
    price,
    strike: null,
    source: `${t("order:catalog.basePrice")}${giftSuffix}`,
    sourceIsPromo: false,
    tag: nonPrice ? nonPriceTag(t, nonPrice) : null,
    retail,
  }
}

type PromoRuleLike = {
  pricingType: string
  discountPercentage?: number | null
  discountAmount?: number | null
  fixedPrice?: number | null
  freeGoodsRule?: ApplicablePromotion["freeGoodsRule"]
  bundleConfig?: ApplicablePromotion["bundleConfig"]
  thresholdRule?: ApplicablePromotion["thresholdRule"]
}

/** One-line rule text for a promotion card / row. */
export const describePromotion = (t: TFunction, promo: PromoRuleLike): string => {
  switch (promo.pricingType) {
    case "DISCOUNT":
      if (pct(promo.discountPercentage)) return t("order:catalog.promoDesc.discountPct", { pct: pct(promo.discountPercentage) })
      if (promo.discountAmount) return t("order:catalog.promoDesc.discountAmount", { amount: currencyFormat(promo.discountAmount) })
      return t("order:catalog.promoDesc.generic")
    case "FIXED":
      return promo.fixedPrice
        ? t("order:catalog.promoDesc.fixed", { price: currencyFormat(promo.fixedPrice) })
        : t("order:catalog.promoDesc.generic")
    case "TIER":
      return t("order:catalog.promoDesc.tier")
    case "FREE_GOODS":
      return promo.freeGoodsRule
        ? t("order:catalog.promoDesc.gift", { buy: promo.freeGoodsRule.buyQuantity, get: promo.freeGoodsRule.getQuantity })
        : t("order:catalog.promoDesc.generic")
    case "BUNDLE":
      return t("order:catalog.promoDesc.bundle")
    case "THRESHOLD": {
      const rule = promo.thresholdRule
      if (!rule) return t("order:catalog.promoDesc.generic")
      const discount = rule.discountPercentage
        ? t("order:productList.thresholdPercentDiscount", { percent: pct(rule.discountPercentage) })
        : t("order:productList.thresholdAmountDiscount", { amount: currencyFormat(rule.discountAmount || 0) })
      // de-price-service promotion Joi: ORDER_AMOUNT | ORDER_QUANTITY | CATEGORY_AMOUNT | VARIANT_QUANTITY
      return rule.thresholdType === "ORDER_QUANTITY" || rule.thresholdType === "VARIANT_QUANTITY"
        ? t("order:catalog.promoDesc.thresholdQty", { value: rule.thresholdValue, discount })
        : t("order:catalog.promoDesc.thresholdAmount", { value: currencyFormat(rule.thresholdValue), discount })
    }
    default:
      return t("order:catalog.promoDesc.generic")
  }
}

export type PromoGroup = {
  promotionId: string
  name: string
  kind: PromoKind
  effectiveTo: string | null
  rule: PromoRuleLike
  products: any[]
  supplierNames: string[]
}

/** Every promotion touching the given products, from their price breakdowns. */
export const buildPromoGroups = (products: any[], breakdowns: Record<string, PriceBreakdown>): PromoGroup[] => {
  const groups = new Map<string, PromoGroup>()
  const add = (id: string, name: string, kind: string, effectiveTo: string | null, rule: PromoRuleLike, product: any) => {
    if (!PROMO_KINDS.includes(kind as PromoKind)) return
    let g = groups.get(id)
    if (!g) {
      g = { promotionId: id, name, kind: kind as PromoKind, effectiveTo, rule, products: [], supplierNames: [] }
      groups.set(id, g)
    }
    if (!g.products.includes(product)) g.products.push(product)
    if (product.supplierName && !g.supplierNames.includes(product.supplierName)) g.supplierNames.push(product.supplierName)
  }
  for (const product of products) {
    const b = breakdowns[product.id]
    if (!b) continue
    if (b.selectedPriceType === "PROMOTION" && b.promotionPrice) {
      const p = b.promotionPrice
      add(p.promotionId, p.promotionName, p.pricingType, p.effectiveTo, p, product)
    }
    for (const ap of b.applicablePromotions || []) {
      add(ap.promotionId, ap.promotionName, ap.pricingType, ap.effectiveTo, ap, product)
    }
  }
  return Array.from(groups.values()).sort((a, b) => (a.effectiveTo || "9999").localeCompare(b.effectiveTo || "9999"))
}

/** Whole calendar days until the end date; null without one. */
export const daysUntil = (value?: string | null): number | null => {
  if (!value) return null
  const end = dayjs(value)
  if (!end.isValid()) return null
  return end.startOf("day").diff(dayjs().startOf("day"), "day")
}

/** Whether a product carries any promotion the buyer can see. */
export const hasPromotion = (b: PriceBreakdown | undefined): boolean =>
  !!b && ((b.selectedPriceType === "PROMOTION" && !!b.promotionPrice) || (b.applicablePromotions || []).length > 0)

/** Why the add-to-cart control is blocked, or null when it is not. */
export const addBlockReason = (
  t: TFunction,
  { price, stock, minOrder, sellWithoutStock }: { price: number; stock: number; minOrder: number; sellWithoutStock: boolean },
): string | null => {
  if (!(price > 0)) return t("order:catalog.reasonNoPrice")
  if (!sellWithoutStock && stock <= 0) return t("order:catalog.reasonOutOfStock")
  if (!sellWithoutStock && minOrder > stock) return t("order:catalog.reasonBelowMin", { count: minOrder })
  return null
}
